import React from 'react'
import { connect } from 'react-redux'
import styles from './GuidesList.scss'
import { sharedDroppableStyle, sharedPlaceholderStyle } from '../../ExperiencesApp'
import { SectionBox } from 'common/styleguide/SectionBox'
import { CTA } from 'common/styleguide/CTA'
import { GuidePageListItem, WithPortal } from './GuidePageListItem'
import { experienceGuidePageActions, experienceGuidePageSelectors } from 'data/resources/experienceGuidePages'
import { ExperienceGuidePage } from 'data/records'
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd'
import { Map } from 'immutable'
import _ from 'lodash'

// helper function
const reorder = (list, startI, endI) => {
  const result = Array.from(list)
  const [removed] = result.splice(startI, 1)
  result.splice(endI, 0, removed)

  return result
}

export class _GuidesList extends React.PureComponent {
  constructor (props) {
    super(props)

    this.state = {
      sectionName: '',
      draggingId: null,
      pendingPostions: null, // all guide page postions during reorder
      pendingNestedPostions: null, // for to level postions during reorder
      createPending: false,
      createSuccess: false,
      createError: false
    }
  }

  addSectionTitleChanged = (e) => {
    this.setState({ sectionName: e.target.value })
  }

  addGuidePage = (e, isHeader) => {
    e.preventDefault()

    const { experience, createGuidePage, setExpanded } = this.props
    const { sectionName } = this.state

    if (!sectionName) return

    this.setState({ createPending: true, createSuccess: false, createError: false }, () => {
      createGuidePage(ExperienceGuidePage({ experienceId: experience.id, title: sectionName, isHeader }))
        .then((r) => {
          setExpanded(r.action.payload.experienceGuidePage.id)
          this.setState((state) => ({
            createPending: false,
            createSuccess: true,
            sectionName: ''
          }))
        })
        .catch(() => this.setState({ createPending: false, createError: true }))
    })
  }

  // this is our opportunity to add all our <Droppable />s and <Draggable />s
  // to the page (ie: expand all our guide page headers). react-beautiful-dnd
  // doesn't like such additions to happen during a drag
  onBeforeCapture = (r) => {
    this.setState({ draggingId: r.draggableId })
  }

  // useful: https://codesandbox.io/s/5v2yvpjn7n
  onDragEnd = (result) => {
    if (!result.destination) {
      // dropped outside the list
      this.setState({ draggingId: null })
      return
    }

    const { guides, topGuides, childrenForId, reorderGuidePages, experience } = this.props
    const { source, destination } = result
    const draggableId = parseInt(result.draggableId, 10)
    const sourceI = source.index
    const destI = destination.index
    const sourceDroppableId = source.droppableId
    const destDroppableId = destination.droppableId

    // is this an actionable/valid drop?
    if (sourceDroppableId === destDroppableId && sourceI === destI) {
      // dropped back in same location
      this.setState({ draggingId: null })
      return
    } else if (destDroppableId !== 'topDrop' && guides.get(parseInt(destDroppableId, 10)).isHeader && guides.get(draggableId).isHeader) {
      // header dropped into a header
      this.setState({ draggingId: null })
      return
    }

    // first, determine what our new topIds will be
    const topIds = topGuides.valueSeq().sortBy(g => g.position).map(g => g.id)
    let newTopIds
    if (destDroppableId === 'topDrop' && !topIds.includes(draggableId)) {
      // from a header to the top level
      newTopIds = topIds.toArray()
      newTopIds.splice(destI, 0, draggableId)
    } else if (destDroppableId !== 'topDrop' && topIds.includes(draggableId)) {
      // from the top level to a header
      newTopIds = topIds.filter(id => id !== draggableId).toArray()
    } else if (sourceDroppableId === 'topDrop' && destDroppableId === 'topDrop') {
      // reordering the top level
      newTopIds = reorder(topIds, sourceI, destI)
    } else {
      // reorder is happening within/between headers
      newTopIds = topIds.toArray()
    }

    let orderNested = []
    if (destDroppableId === 'topDrop') {
      // moved to/within top level
      // NOTE: if moving a nested gp to the top level, the backend will null
      // out experienceGuidePageId for us
      newTopIds.map((id, idx) => {
        const children = childrenForId(id).toArray().filter(c => c.id !== draggableId)
        orderNested.push(Object.assign({
          id,
          position: idx
        },
        children.length > 0 && { children }
        ))
      })
    } else {
      // dropping into a header
      const subItemMap = guides.valueSeq().reduce((acc, guide) => {
        if (!guide.experienceGuidePageId) {
          acc = acc.set(guide.id, acc.get(guide.id, [])) // don't overwrite
        } else {
          const next = acc.get(guide.experienceGuidePageId, [])
          next.push(guide.id)
          acc = acc.set(guide.experienceGuidePageId, next)
        }
        return acc
      }, Map())

      const sourceParentId = parseInt(sourceDroppableId, 10)
      const destParentId = parseInt(destDroppableId, 10)
      const sourceSubItems = _.sortBy(subItemMap.get(sourceParentId), gid => guides.get(gid).position)
      const destSubItems = _.sortBy(subItemMap.get(destParentId), gid => guides.get(gid).position)

      if (sourceDroppableId === 'topDrop') {
        // dragging from top level into a header
        let newDestSubItems = [...destSubItems]
        newDestSubItems.splice(destI, 0, draggableId)
        newTopIds.map(id => {
          const guide = guides.get(id)
          let children
          if (id === destParentId) {
            children = newDestSubItems.map((sid, idx) => ({
              id: sid,
              position: idx
            }))
          } else {
            children = childrenForId(id).toArray()
          }

          orderNested.push(Object.assign({
            id: id,
            position: guide.position
          },
          children.length > 0 && { children }
          ))
        })
      } else if (sourceParentId === destParentId) {
        // dragging within a header
        const reordered = reorder(sourceSubItems, sourceI, destI)

        newTopIds.map((id, idx) => {
          const guide = guides.get(id)
          const children = id === sourceParentId
            ? reordered.map((reorderedId, idx) => ({
              id: reorderedId,
              title: guides.get(reorderedId).title,
              position: idx
            }))
            : childrenForId(id).toArray()

          orderNested.push(Object.assign({
            id,
            position: guide.position
          },
          children.length > 0 && { children }
          ))
        })
      } else {
        // dragging between headers
        let newSourceSubItems = sourceSubItems
        let newDestSubItems = destSubItems
        const [draggedItem] = newSourceSubItems.splice(sourceI, 1)
        newDestSubItems.splice(destI, 0, draggedItem)

        newTopIds.map((id, idx) => {
          const guide = guides.get(id)
          let children
          if (id === sourceParentId) {
            children = newSourceSubItems.map((sid, idx) => ({
              id: sid,
              position: idx
            }))
          } else if (id === destParentId) {
            children = newDestSubItems.map((sid, idx) => ({
              id: sid,
              position: idx
            }))
          } else {
            children = childrenForId(id).toArray()
          }

          orderNested.push(Object.assign({
            id,
            position: guide.position
          },
          children.length > 0 && { children }
          ))
        })
      }
    }

    const pendingPositions = Map().withMutations(m => {
      orderNested.forEach(item => {
        m.set(item.id, item.position)
        item.children && item.children.forEach(child => {
          m.set(child.id, child.position)
        })
      })
    })

    this.setState({ pendingPositions, pendingNestedPostions: orderNested }, () => {
      reorderGuidePages({ experienceId: experience.id, orderNested })
        .then(() => this.setState({ pendingPositions: null, pendingNestedPostions: null }))
    })
    this.setState({ draggingId: null },
      () => this.props.setExpanded(parseInt(destDroppableId, 10))
    )
  }

  render () {
    const { guides, topGuides, childrenForId, expandedId, setExpanded } = this.props
    const { sectionName, draggingId, pendingPositions, pendingNestedPostions, createPending } = this.state
    const sortedTopGuides = pendingNestedPostions
      ? Map(pendingNestedPostions.map(item => [item.id, guides.get(item.id)]))
      : topGuides

    return (
      <div className={styles.GuidesList}>
        <SectionBox title='guide' noPadding className={styles.card}>
          <div className={styles.addSection}>
            Add Section or Header
            <form onSubmit={e => e.preventDefault()}>
              <div className={styles.addSectionForm}>
                <div className={styles.input}>
                  <input type='text' placeholder='Enter section header...' onChange={this.addSectionTitleChanged} value={sectionName} />
                </div>
                <div className={styles.button}>
                  <CTA inline secondary margins={false} disabled={createPending} onClick={e => this.addGuidePage(e, true)}>
                    Add Header
                  </CTA>

                  <CTA inline secondary disabled={createPending} onClick={e => this.addGuidePage(e, false)} className={styles.addSectionButton}>
                    Add Section
                  </CTA>
                </div>
              </div>
            </form>
          </div>
          {sortedTopGuides.size > 0 && <DragDropContext
            onBeforeCapture={this.onBeforeCapture}
            onDragEnd={this.onDragEnd}>
            <div className={styles.gpliPortal} ref={r => { this._portalRef = r }} />
            <Droppable droppableId='topDrop' type='droppable'>
              {(provided, snapshot) => (
                <div style={sharedDroppableStyle(snapshot.isDraggingOver)}>
                  <div ref={provided.innerRef}>
                    {sortedTopGuides.valueSeq().map((guide, index) => <Draggable key={guide.id} draggableId={`${guide.id}`} index={index}>
                      {(provided, snapshot) => (
                        <div>
                          <div ref={provided.innerRef} {...provided.draggableProps}>
                            <WithPortal
                              node={<GuidePageListItem
                                draggingId={draggingId}
                                setExpanded={setExpanded}
                                guide={guide}
                                guides={guides}
                                pendingPositions={pendingPositions}
                                childrenForId={childrenForId}
                                expandedId={expandedId}
                                isDragging={snapshot.isDragging}
                                portalRef={this._portalRef}
                                draggableProps={provided.draggableProps}
                                dragHandleProps={provided.dragHandleProps} />}
                              portalRef={this._portalRef}
                              snapshot={snapshot}
                            />
                          </div>
                          {provided.placeholder}
                        </div>
                      )}
                    </Draggable>
                    )}
                  </div>
                  <div style={sharedPlaceholderStyle(snapshot.isDraggingOver)}>
                    {provided.placeholder}
                  </div>
                </div>
              )}
            </Droppable>
          </DragDropContext>
          }
        </SectionBox>
      </div>
    )
  }
}

const mapStateToProps = (state, ownProps) => ({
  topGuides: experienceGuidePageSelectors.topGuides(state, ownProps.experience.id),
  childrenForId: guideId => experienceGuidePageSelectors.childrenForId(state, guideId)
})

const mapDispatchToProps = {
  createGuidePage: experienceGuidePageActions.create,
  reorderGuidePages: experienceGuidePageActions.reorderNested
}

export const GuidesList = connect(
  mapStateToProps,
  mapDispatchToProps
)(_GuidesList)
